import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { evaluationsAPI } from '../lib/api';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { useAuth } from '../context/AuthContext';
import { HelpButton } from '../components/HelpButton';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, ClipboardCheck, FileText, AlertTriangle, Clock, RefreshCw, PenLine, Users, ArrowUp, ArrowDown, ArrowUpDown, ShieldCheck } from 'lucide-react';
import { SearchableCombobox } from '../components/SearchableCombobox';
import { formatDate } from '../lib/utils';

const STATUS_COLORS = {
  draft: 'bg-slate-200 text-slate-700',
  awaiting_employee: 'bg-amber-100 text-amber-800',
  awaiting_supervisor: 'bg-orange-100 text-orange-800',
  awaiting_reviewer: 'bg-blue-100 text-blue-800',
  in_review: 'bg-amber-100 text-amber-800',
  pending_reviewer: 'bg-blue-100 text-blue-800',
  pending_hr: 'bg-indigo-100 text-indigo-800',
  complete: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-800',
};

const STATUS_LABELS = {
  draft: 'Draft',
  // These three were missing, so the list rendered the raw internal strings
  // ("awaiting_employee") straight at the user.
  awaiting_employee: 'Employee to Sign',
  awaiting_supervisor: 'Supervisor to Sign',
  awaiting_reviewer: 'Reviewer to Sign',
  in_review: 'Employee + Supervisor',
  pending_reviewer: 'Reviewer Pending',
  pending_hr: 'HR Pending',
  complete: 'Complete',
  declined: 'Declined',
};

// Must match EVALUATION_TYPES in backend/evaluation_routes.py.
const EVAL_TYPES = ['Annual', '90-Day Trial', '6-Month'];

// Must match RATING_BANDS in backend/evaluation_routes.py.
const RATING_BANDS = [
  'Outstanding',
  'Exceeds Expectations',
  'Meets Expectations',
  'Needs Improvement',
  'Unsatisfactory',
];

const BAND_COLORS = {
  'Outstanding': 'bg-emerald-100 text-emerald-800',
  'Exceeds Expectations': 'bg-teal-100 text-teal-800',
  'Meets Expectations': 'bg-slate-100 text-slate-700',
  'Needs Improvement': 'bg-amber-100 text-amber-800',
  'Unsatisfactory': 'bg-red-100 text-red-800',
};

function EvalSortHeader({ label, colKey, sortKey, sortDir, onSort }) {
  const active = sortKey === colKey;
  return (
    <TableHead
      onClick={() => onSort(colKey)}
      data-testid={`sort-${colKey}`}
      className="cursor-pointer select-none hover:text-slate-900 transition-colors"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active
          ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </TableHead>
  );
}

export default function Evaluations() {
  const { hasRole } = useAuth();
  const perms = useEffectivePermissions();
  const canOversee = perms.can('evaluation_oversight', 'view');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [evaluations, setEvaluations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [bandFilter, setBandFilter] = useState('all');
  // Sorted in the database, not the browser: the list is capped at 500, so
  // sorting client-side would only ever order the newest 500 rows.
  const [sortKey, setSortKey] = useState('created');
  const [sortDir, setSortDir] = useState('desc');
  const [sigAudit, setSigAudit] = useState(null); // null=closed, {} while open
  const [sigAuditBusy, setSigAuditBusy] = useState(false);

  const runSignatureIntegrity = async () => {
    setSigAudit({ count: 0, items: [] });
    setSigAuditBusy(true);
    try {
      const r = await evaluationsAPI.signatureIntegrity();
      setSigAudit(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run the signature check');
      setSigAudit(null);
    } finally { setSigAuditBusy(false); }
  };
  const reopenReviewer = async (evaluationId, part) => {
    const reason = window.prompt('Reason for reopening the reviewer signature (required). The old signature is kept in the audit history and the eligible APM/PM is asked to sign again.');
    if (reason === null) return;
    if (!reason.trim()) { toast.error('A reason is required.'); return; }
    try {
      await evaluationsAPI.reopenReviewer(evaluationId, reason.trim(), part);
      toast.success('Reviewer signature reopened — the eligible APM/PM has been notified to sign.');
      runSignatureIntegrity();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reopen the reviewer signature');
    }
  };
  const [showNew, setShowNew] = useState(false);
  const [stuck, setStuck] = useState([]);
  const [stuckLoading, setStuckLoading] = useState(true);
  const [regenId, setRegenId] = useState('');
  // Phase 2.5: personal + org-wide sign queues
  const [myQueue, setMyQueue] = useState([]);
  const [allQueue, setAllQueue] = useState([]);

  // New evaluation state
  const [newEmpId, setNewEmpId] = useState('');
  const [newEvalType, setNewEvalType] = useState('Annual');
  const [newPeriodFrom, setNewPeriodFrom] = useState('');
  const [newPeriodTo, setNewPeriodTo] = useState('');
  const [creating, setCreating] = useState(false);
  // 2026-08-22: track selector + hire-date-driven due-date preview.
  const [availableTracks, setAvailableTracks] = useState([{ id: 'security_officer', label: 'Security Officer' }]);
  const [newEvalTrack, setNewEvalTrack] = useState('security_officer');
  // 2026-06-26: named signers. The completing supervisor is mandatory; a named
  // reviewer is optional (blank = every APM-and-above on the client team is emailed).
  const [signers, setSigners] = useState({ authors: [], reviewers: [], my_employee_id: null });
  const [signersLoading, setSignersLoading] = useState(false);
  const [newCompletingSup, setNewCompletingSup] = useState('');
  const [newReviewer, setNewReviewer] = useState('');
  const [selectedEmpHireDate, setSelectedEmpHireDate] = useState('');
  // Load track eligibility whenever the selected employee changes so GSOC only
  // appears for Surveillance employees.
  useEffect(() => {
    if (!newEmpId) {
      setAvailableTracks([{ id: 'security_officer', label: 'Security Officer' }]);
      setSelectedEmpHireDate('');
      return;
    }
    const emp = employees.find(e => e.id === newEmpId);
    setSelectedEmpHireDate(emp?.hire_date || '');
    (async () => {
      try {
        const r = await evaluationsAPI.getTracks(newEmpId);
        setAvailableTracks(r.data || [{ id: 'security_officer', label: 'Security Officer' }]);
      } catch (_) {
        setAvailableTracks([{ id: 'security_officer', label: 'Security Officer' }]);
      }
    })();
  }, [newEmpId, employees]);
  // Compute the calculated 90-day due date preview from the selected employee's hire date.
  const calculatedDueDate = (() => {
    if (newEvalType !== '90-Day Trial' || !selectedEmpHireDate) return '';
    try {
      // Date-only hire date: do the 90-day math entirely in UTC so the calendar
      // day never shifts with the browser timezone. new Date("YYYY-MM-DD") is
      // UTC midnight, but getDate/setDate read/write LOCAL parts — mixing the
      // two drifts the result a day west of GMT.
      const [y, m, dd] = String(selectedEmpHireDate).slice(0, 10).split('-').map(Number);
      const d = new Date(Date.UTC(y, m - 1, dd));
      d.setUTCDate(d.getUTCDate() + 90);
      return d.toISOString().split('T')[0];
    } catch (_) { return ''; }
  })();


  // A 90-Day Trial covers hire date → hire + 90, so fill the period in rather
  // than making someone work it out. Only fills EMPTY fields, so a deliberate
  // choice is never overwritten. This is what makes a My Work reminder one
  // click of work instead of a form.
  useEffect(() => {
    if (newEvalType !== '90-Day Trial' || !selectedEmpHireDate || !calculatedDueDate) return;
    setNewPeriodFrom(prev => prev || String(selectedEmpHireDate).slice(0, 10));
    setNewPeriodTo(prev => prev || calculatedDueDate);
  }, [newEvalType, selectedEmpHireDate, calculatedDueDate]);

  useEffect(() => { load(); loadStuck(); loadQueues(); }, []);

  // Sorting and the type/band filters are server-side, so re-fetch when they change.
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
    [sortKey, sortDir, typeFilter, bandFilter]);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Names and types read best A→Z; dates and scores best highest-first.
      setSortDir(['employee', 'type', 'status'].includes(key) ? 'asc' : 'desc');
    }
  };

  // Phase 2.5: load "my sign queue" for the logged-in user, plus the org-wide
  // queue when they're admin/HR. Fail silently — endpoints return [] for users
  // with no queue rather than 404-ing.
  const loadQueues = async () => {
    try {
      const my = await evaluationsAPI.mySignQueue();
      setMyQueue(my.data || []);
    } catch (_e) { setMyQueue([]); }
    // Org-wide queue: gated on the evaluation-oversight capability, not on a
    // role name (2026-09-30). A manager holds `evaluations:view` to evaluate
    // their own team and must not see every employee's pending evaluation.
    if (canOversee) {
      try {
        const all = await evaluationsAPI.allSignQueue();
        setAllQueue(all.data || []);
      } catch (_e) { setAllQueue([]); }
    }
  };

  // Quick-action deep link: /evaluations?new=<employee_id> pre-opens the dialog.
  // &type=<evaluation type> also preselects the type, which is how a hire-date
  // reminder in My Work turns into one click of work.
  useEffect(() => {
    const newEmp = searchParams.get('new');
    if (newEmp) {
      setNewEmpId(newEmp);
      const t = searchParams.get('type');
      if (t && EVAL_TYPES.includes(t)) setNewEvalType(t);
      setShowNew(true);
      searchParams.delete('new');
      searchParams.delete('type');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!newEmpId) { setSigners({ authors: [], reviewers: [], my_employee_id: null }); setNewCompletingSup(''); setNewReviewer(''); return; }
    let cancelled = false;
    setSignersLoading(true);
    evaluationsAPI.evalSigners(newEmpId)
      .then(res => {
        if (cancelled) return;
        const d = res.data || {};
        setSigners(d);
        // Default to myself when I'm eligible to author for this employee.
        const mine = (d.authors || []).find(a => a.id === d.my_employee_id);
        setNewCompletingSup(mine ? mine.id : '');
        setNewReviewer('');
      })
      .catch(() => { if (!cancelled) setSigners({ authors: [], reviewers: [], my_employee_id: null }); })
      .finally(() => { if (!cancelled) setSignersLoading(false); });
    return () => { cancelled = true; };
  }, [newEmpId]);

  const load = async () => {
    setLoading(true);
    try {
      const [evRes, empRes] = await Promise.all([
        evaluationsAPI.list({
          sort: sortKey,
          direction: sortDir,
          ...(typeFilter !== 'all' ? { evaluation_type: typeFilter } : {}),
          ...(bandFilter !== 'all' ? { band: bandFilter } : {}),
        }),
        // Span-scoped picker off evaluations:view — deliberately NOT the
        // employees roster, which a GSOC Supervisor has no access to.
        evaluationsAPI.eligibleEmployees(),
      ]);
      setEvaluations(evRes.data);
      setEmployees(empRes.data);
    } catch (e) {
      toast.error('Failed to load evaluations');
    } finally {
      setLoading(false);
    }
  };

  const loadStuck = async () => {
    setStuckLoading(true);
    try {
      const res = await evaluationsAPI.stuckAtEmployee();
      setStuck(res.data || []);
    } catch (e) {
      // Non-fatal — stuck endpoint only fails on permission; just hide the card
      setStuck([]);
    } finally {
      setStuckLoading(false);
    }
  };

  const handleRegen = async (evId) => {
    setRegenId(evId);
    try {
      await evaluationsAPI.regenerateEmployeeLink(evId);
      toast.success('A new signing link was emailed to the employee.');
      loadStuck();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not regenerate link');
    } finally {
      setRegenId('');
    }
  };

  const filtered = evaluations.filter(ev => {
    if (statusFilter !== 'all' && ev.status !== statusFilter) return false;
    if (employeeFilter && ev.employee_id !== employeeFilter) return false;
    return true;
  });

  const empOptions = employees.map(e => ({
    _key: e.id,
    _searchStr: `${e.first_name} ${e.last_name} ${e.employee_number || ''}`.toLowerCase(),
    ...e,
  }));

  const handleCreate = async () => {
    if (!newEmpId || !newPeriodFrom || !newPeriodTo) {
      toast.error('Employee and period dates are required');
      return;
    }
    if (!newCompletingSup) {
      toast.error('Select which supervisor is completing this evaluation');
      return;
    }
    setCreating(true);
    try {
      const res = await evaluationsAPI.create({
        employee_id: newEmpId,
        period_from: newPeriodFrom,
        period_to: newPeriodTo,
        evaluation_type: newEvalType,
        evaluation_track: newEvalTrack,
        completing_supervisor_id: newCompletingSup,
        selected_reviewer_id: newReviewer || null,
      });
      toast.success('Evaluation created');
      setShowNew(false);
      setNewEmpId(''); setNewEvalType('Annual'); setNewPeriodFrom(''); setNewPeriodTo('');
      setNewCompletingSup(''); setNewReviewer('');
      navigate(`/evaluations/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create evaluation');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="evaluations-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Evaluations</h1>
          <p className="text-slate-500 mt-1">Annual Security Officer Performance Evaluations</p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton module="evaluations" />
          {canOversee && (
            <Button variant="outline" onClick={runSignatureIntegrity} data-testid="signature-integrity-btn">
              <ShieldCheck className="w-4 h-4 mr-2" /> Signature check
            </Button>
          )}
          {/* Mirrors the API gate `_cap_or_roles('evaluations','add', ADMIN, HR, MANAGER)`.
              Was RequireCap action="add", which is capability-only — HR holds
              evaluations:[view,edit] and so never saw this button even though the
              API lets HR author, and the same button on the employee record did
              show. Code review 2026-09-09. */}
          {(perms.can('evaluations', 'add') || hasRole('admin', 'hr', 'manager')) && (
            <Button onClick={() => setShowNew(true)} data-testid="new-evaluation-btn">
              <Plus className="w-4 h-4 mr-2" /> New Evaluation
            </Button>
          )}
        </div>
      </div>

      <Dialog open={sigAudit !== null} onOpenChange={(o) => { if (!o) setSigAudit(null); }}>
        <DialogContent className="max-w-3xl" data-testid="signature-integrity-dialog">
          <DialogHeader>
            <DialogTitle>Signature integrity check</DialogTitle>
            <DialogDescription>
              Signature-integrity review (admin & HR). Flags employee signatures recorded by staff instead of the secure link, reviewer lines signed below APM/PM level or by someone who signed another line or auto-filled from the supervisor, and teams with no eligible reviewer. Nothing is changed unless you reopen a reviewer line.
            </DialogDescription>
          </DialogHeader>
          {sigAuditBusy ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="signature-integrity-loading">Checking…</p>
          ) : ((sigAudit?.items || []).length === 0 && (sigAudit?.reviewer_violations || []).length === 0 && (sigAudit?.team_warnings || []).length === 0) ? (
            <p className="text-sm text-emerald-700 py-6 text-center" data-testid="signature-integrity-clean">
              ✓ No signature problems found — every signature is through the secure link and every reviewer line is valid.
            </p>
          ) : (
            <div className="max-h-[26rem] overflow-auto space-y-5">
              {(sigAudit?.items || []).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-amber-800 mb-2" data-testid="signature-integrity-count">
                    {sigAudit.count} employee signature{sigAudit.count === 1 ? '' : 's'} recorded by a staff user instead of the secure link.
                  </p>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Employee</TableHead><TableHead>Type / Part</TableHead>
                      <TableHead>Recorded by</TableHead><TableHead>When</TableHead><TableHead></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {sigAudit.items.map((r, i) => (
                        <TableRow key={r.evaluation_id + r.part} data-testid={`signature-integrity-row-${i}`}>
                          <TableCell className="font-medium">{r.employee_name}</TableCell>
                          <TableCell>{r.evaluation_type} · {r.part}</TableCell>
                          <TableCell className="text-red-700">{r.recorded_by}</TableCell>
                          <TableCell>{r.signed_at ? formatDate(r.signed_at) : '—'}</TableCell>
                          <TableCell><Link to={`/evaluations/${r.evaluation_id}`} className="text-blue-600 hover:underline text-sm">Open</Link></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {(sigAudit?.reviewer_violations || []).length > 0 && (
                <div data-testid="signature-integrity-reviewer-section">
                  <p className="text-sm font-medium text-amber-800 mb-2" data-testid="signature-integrity-reviewer-count">
                    {sigAudit.reviewer_violations.length} reviewer-line problem{sigAudit.reviewer_violations.length === 1 ? '' : 's'}. The reviewer must be an Assistant PM or PM on the employee's team.
                  </p>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Employee</TableHead><TableHead>Type / Part</TableHead>
                      <TableHead>Reviewer signed by</TableHead><TableHead>Why flagged</TableHead>
                      <TableHead>When</TableHead><TableHead></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {sigAudit.reviewer_violations.map((r, i) => (
                        <TableRow key={r.evaluation_id + r.part} data-testid={`signature-integrity-reviewer-row-${i}`}>
                          <TableCell className="font-medium">{r.employee_name}</TableCell>
                          <TableCell>{r.evaluation_type} · {r.part}</TableCell>
                          <TableCell className="text-red-700">{r.reviewer_signed_by}{r.signer_level_label ? ` (${r.signer_level_label})` : ''}</TableCell>
                          <TableCell className="text-xs text-amber-800">{(r.reasons || []).join('; ')}</TableCell>
                          <TableCell>{r.signed_at ? formatDate(r.signed_at) : '—'}</TableCell>
                          <TableCell>
                            {canOversee
                              ? <Button size="sm" variant="outline" data-testid={`reopen-reviewer-${i}`} onClick={() => reopenReviewer(r.evaluation_id, r.part)}>Reopen for reviewer</Button>
                              : <Link to={`/evaluations/${r.evaluation_id}`} className="text-blue-600 hover:underline text-sm">Open</Link>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {(sigAudit?.team_warnings || []).length > 0 && (
                <div data-testid="signature-integrity-team-section">
                  <p className="text-sm font-medium text-amber-800 mb-2" data-testid="signature-integrity-team-count">
                    {sigAudit.team_warnings.length} supervisory team{sigAudit.team_warnings.length === 1 ? '' : 's'} where a reviewer cannot be drawn (supervisor-level only, no APM/PM).
                  </p>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Client / Team</TableHead><TableHead>Levels present</TableHead><TableHead>Issue</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {sigAudit.team_warnings.map((t, i) => (
                        <TableRow key={t.client_id} data-testid={`signature-integrity-team-row-${i}`}>
                          <TableCell className="font-medium">{t.client_name}</TableCell>
                          <TableCell className="text-xs">{(t.levels_present || []).join(', ').toUpperCase()}</TableCell>
                          <TableCell className="text-xs text-amber-800">{t.issue}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 2.5: personal sign queue — supervisors and reviewers/managers
          see every eval where THEY are the next signer. Empty state hidden. */}
      {myQueue.length > 0 && (
        <Card className="border-blue-400 bg-blue-50" data-testid="my-sign-queue-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <PenLine className="w-5 h-5" /> Your Sign Queue
              <Badge className="ml-1 bg-blue-700 text-blue-50">{myQueue.length}</Badge>
            </CardTitle>
            <CardDescription className="text-blue-800">
              Evaluations waiting on your signature. Oldest first — please sign as soon as you can.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Your Role</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {myQueue.map(ev => {
                  const snap = ev.employee_snapshot || {};
                  return (
                    <TableRow key={ev.id} data-testid={`my-queue-row-${ev.id}`}>
                      <TableCell className="font-medium">{snap.first_name} {snap.last_name}
                        {ev.credential_headsup_no_email && (
                          <Badge className="ml-2 bg-amber-100 text-amber-800" data-testid={`headsup-no-email-flag-${ev.id}`} title="No email on file — credentials heads-up not sent">No email on file</Badge>
                        )}
                      </TableCell>
                      <TableCell><Badge variant="outline">{ev.evaluation_type || 'Annual'}</Badge></TableCell>
                      <TableCell className="text-sm">{ev.period_from} → {ev.period_to}</TableCell>
                      <TableCell>
                        <Badge className={ev.your_role === 'reviewer' ? 'bg-purple-100 text-purple-800' : 'bg-emerald-100 text-emerald-800'}>
                          {ev.your_role === 'reviewer' ? 'Reviewer / Manager' : 'Supervisor'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button size="sm" onClick={() => navigate(`/evaluations/${ev.id}`)} data-testid={`open-sign-btn-${ev.id}`}>
                          Open &amp; Sign
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Admin/HR only: org-wide pending signatures (not just yours) */}
      {canOversee && allQueue.length > 0 && (() => {
        // Filter out rows already in the personal "Your Sign Queue" card so
        // the admin card is strictly "others' queue items" — matches the copy
        // that says the admin is not the direct signer for these.
        const myIds = new Set(myQueue.map(m => m.id));
        const othersQueue = allQueue.filter(ev => !myIds.has(ev.id));
        if (othersQueue.length === 0) return null;
        return (
        <Card className="border-slate-300" data-testid="all-sign-queue-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" /> Org-wide Pending Signatures
              <Badge variant="outline" className="ml-1">{othersQueue.length}</Badge>
            </CardTitle>
            <CardDescription>
              Every evaluation sitting in another supervisor&apos;s or reviewer&apos;s queue. Use for bottleneck triage — you are not the direct signer for these.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Waiting On</TableHead>
                  <TableHead>Days Stuck</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {othersQueue.map(ev => {
                  const snap = ev.employee_snapshot || {};
                  const nextSigner = ev.status === 'awaiting_supervisor'
                    ? (snap.direct_supervisor_name || '—')
                    : (snap.reviewer_name || '—');
                  const roleTag = ev.status === 'awaiting_supervisor' ? 'Supervisor' : 'Reviewer';
                  return (
                    <TableRow key={ev.id} data-testid={`all-queue-row-${ev.id}`}>
                      <TableCell className="font-medium">{snap.first_name} {snap.last_name}</TableCell>
                      <TableCell><span className="text-slate-600">{roleTag}:</span> <b>{nextSigner}</b></TableCell>
                      <TableCell>{ev.days_stuck != null ? `${ev.days_stuck} d` : '—'}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/evaluations/${ev.id}`)}>Open</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        );
      })()}

      {/* Stuck-at-employee panel */}
      {!stuckLoading && stuck.length > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="stuck-evals-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <AlertTriangle className="w-5 h-5" /> Needs Attention
            </CardTitle>
            <CardDescription className="text-amber-800">
              {stuck.length} evaluation{stuck.length === 1 ? '' : 's'} waiting on the employee&apos;s signature. Click to open or resend the link.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Link Status</TableHead>
                  <TableHead>Reminders Sent</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stuck.map(ev => {
                  const snap = ev.employee_snapshot || {};
                  const isExpired = ev.is_expired;
                  const days = ev.days_since_submitted;
                  const left = ev.days_until_expiry;
                  return (
                    <TableRow key={ev.id} className="hover:bg-amber-100">
                      <TableCell className="font-medium">
                        <button className="hover:underline text-left" onClick={() => navigate(`/evaluations/${ev.id}`)} data-testid={`open-stuck-${ev.id}`}>
                          {snap.first_name} {snap.last_name}
                        </button>
                        <div className="text-xs text-slate-500">{snap.email || 'no email on file'}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-slate-500" />
                          {days != null ? `${days}d ago` : '—'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isExpired ? (
                          <Badge className="bg-red-100 text-red-800">Expired</Badge>
                        ) : (
                          <Badge className="bg-amber-200 text-amber-900">{left != null ? `${left}d left` : 'active'}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {ev.reminder_5day_sent_at ? <div>5-day ✓</div> : null}
                        {ev.expired_notice_sent_at ? <div>Expired ✓</div> : null}
                        {!ev.reminder_5day_sent_at && !ev.expired_notice_sent_at ? <span className="text-slate-400">—</span> : null}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" onClick={() => handleRegen(ev.id)} disabled={regenId === ev.id} data-testid={`regen-stuck-${ev.id}`}>
                          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${regenId === ev.id ? 'animate-spin' : ''}`} />
                          {regenId === ev.id ? 'Sending...' : 'Resend Link'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Employee</Label>
              <SearchableCombobox
                items={empOptions}
                value={employeeFilter}
                onSelect={(v) => setEmployeeFilter(v === employeeFilter ? '' : v)}
                placeholder="All employees"
                searchPlaceholder="Search employees..."
                emptyText="No employees"
                testId="filter-employee"
                renderItem={(e) => e ? `${e.first_name} ${e.last_name}` : null}
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {EVAL_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Rating</Label>
              <Select value={bandFilter} onValueChange={setBandFilter}>
                <SelectTrigger data-testid="filter-band">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ratings</SelectItem>
                  {RATING_BANDS.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                  <SelectItem value="unscored">Not yet scored</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="awaiting_employee">Employee to Sign</SelectItem>
                  <SelectItem value="awaiting_supervisor">Supervisor to Sign</SelectItem>
                  <SelectItem value="awaiting_reviewer">Reviewer to Sign</SelectItem>
                  <SelectItem value="in_review">Employee + Supervisor</SelectItem>
                  <SelectItem value="pending_reviewer">Reviewer Pending</SelectItem>
                  <SelectItem value="pending_hr">HR Pending</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                data-testid="clear-filters"
                onClick={() => {
                  setStatusFilter('all'); setEmployeeFilter('');
                  setTypeFilter('all'); setBandFilter('all');
                }}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{filtered.length} Evaluation{filtered.length === 1 ? '' : 's'}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-48 skeleton-pulse rounded-md" />
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No evaluations to display</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <EvalSortHeader label="Employee" colKey="employee" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <EvalSortHeader label="Type" colKey="type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <EvalSortHeader label="Period" colKey="period" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <EvalSortHeader label="Status" colKey="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <EvalSortHeader label="Overall" colKey="overall" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <EvalSortHeader label="Created" colKey="created" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(ev => {
                  const snap = ev.employee_snapshot || {};
                  return (
                    <TableRow key={ev.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/evaluations/${ev.id}`)}>
                      <TableCell className="font-medium">{snap.first_name} {snap.last_name}</TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline" className="font-normal">{ev.evaluation_type || 'Annual'}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{ev.period_from} → {ev.period_to}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[ev.status] || 'bg-slate-200'}>{STATUS_LABELS[ev.status] || ev.status}</Badge>
                        <span
                          className={`ml-2 text-[10px] ${(ev.signing_order || 'old') === 'new' ? 'text-emerald-700' : 'text-slate-400'}`}
                          title={(ev.signing_order || 'old') === 'new'
                            ? 'New order: Supervisor → Reviewer → Employee'
                            : 'Legacy order: Employee → Supervisor → Reviewer'}
                          data-testid={`eval-order-${ev.id}`}
                        >
                          {(ev.signing_order || 'old') === 'new' ? 'New order' : 'Legacy'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {ev.overall_average != null ? (
                          <span>
                            <span className="font-mono font-semibold">{ev.overall_average.toFixed(2)}</span>
                            {ev.overall_band && (
                              <Badge className={`ml-2 text-xs font-normal ${BAND_COLORS[ev.overall_band] || 'bg-slate-100 text-slate-700'}`}>
                                {ev.overall_band}
                              </Badge>
                            )}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(ev.created_at)}</TableCell>
                      <TableCell>
                        <Link to={`/evaluations/${ev.id}`} className="text-blue-600 text-sm hover:underline" onClick={(e) => e.stopPropagation()}>
                          <FileText className="w-4 h-4 inline" /> Open
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Evaluation</DialogTitle>
            <DialogDescription>Select an employee and the period this evaluation covers.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1">
              <Label>Employee *</Label>
              <SearchableCombobox
                items={empOptions}
                value={newEmpId}
                onSelect={setNewEmpId}
                placeholder="Search employee..."
                searchPlaceholder="Type a name..."
                emptyText="No employees"
                testId="new-eval-employee"
                renderItem={(e) => e ? `${e.first_name} ${e.last_name}${e.employee_number ? ` (${e.employee_number})` : ''}` : null}
              />
            </div>
            <div className="space-y-1">
              <Label>Evaluation Type *</Label>
              <Select value={newEvalType} onValueChange={setNewEvalType}>
                <SelectTrigger data-testid="new-eval-type"><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Annual">Annual (calendar year review)</SelectItem>
                  <SelectItem value="90-Day Trial">90-Day Trial (new hire probation)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                6-Month Review is part of the same 90-Day evaluation. Open the completed 90-Day and use the 6-Month tab.
              </p>
            </div>
            {/* Only show the track selector when more than one track is eligible for
                this employee — Security Officer is the default. GSOC only appears
                for Surveillance employees once the GSOC template is content_ready. */}
            {availableTracks.length > 1 && (
              <div className="space-y-1">
                <Label>Evaluation Track *</Label>
                <Select value={newEvalTrack} onValueChange={setNewEvalTrack}>
                  <SelectTrigger data-testid="new-eval-track"><SelectValue placeholder="Select track..." /></SelectTrigger>
                  <SelectContent>
                    {availableTracks.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Hire-date + calculated due-date preview for 90-Day Trials. Read-only
                context so the manager can see the basis for the calculation. */}
            {newEvalType === '90-Day Trial' && selectedEmpHireDate && (
              <div className="grid grid-cols-2 gap-3 rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
                <div>
                  <div className="text-slate-500 text-xs">Hire Date</div>
                  <div className="font-medium text-slate-900" data-testid="new-eval-hire-date">{selectedEmpHireDate}</div>
                </div>
                <div>
                  <div className="text-slate-500 text-xs">Calculated 90-Day Due</div>
                  <div className="font-medium text-slate-900" data-testid="new-eval-calc-due">{calculatedDueDate || '—'}</div>
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label>Completing Supervisor *</Label>
              <Select value={newCompletingSup} onValueChange={setNewCompletingSup} disabled={!newEmpId || signersLoading}>
                <SelectTrigger data-testid="new-eval-completing-supervisor">
                  <SelectValue placeholder={
                    !newEmpId ? 'Pick an employee first'
                      : signersLoading ? 'Loading supervisory team...'
                      : (signers.authors || []).length === 0 ? 'No eligible supervisor on this client team'
                      : 'Select who is completing this evaluation...'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {(signers.authors || []).map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name} — {a.level_label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Only this person signs the supervisor slot, and they are the only one emailed once the employee signs.
              </p>
              {newEmpId && !signersLoading && (signers.authors || []).length === 0 && (
                <p className="text-xs text-amber-600" data-testid="new-eval-no-authors">
                  Nobody on this employee's client supervisory team can author an evaluation. Assign the employee to a
                  client and build its supervisory team first.
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Reviewer <span className="text-xs text-slate-400">(optional)</span></Label>
              <Select value={newReviewer || 'all'} onValueChange={(v) => setNewReviewer(v === 'all' ? '' : v)} disabled={!newEmpId || signersLoading}>
                <SelectTrigger data-testid="new-eval-reviewer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Assistant PM and above (all are emailed)</SelectItem>
                  {(signers.reviewers || []).filter(r => r.id !== newCompletingSup).map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name} — {r.level_label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Leave as-is and every Assistant PM, PM and Regional Manager on the client team is emailed for review —
                whoever acts first clears it.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Period From *</Label>
                <Input type="date" value={newPeriodFrom} onChange={(e) => setNewPeriodFrom(e.target.value)} data-testid="new-eval-period-from" />
              </div>
              <div className="space-y-1">
                <Label>Period To *</Label>
                <Input type="date" value={newPeriodTo} onChange={(e) => setNewPeriodTo(e.target.value)} data-testid="new-eval-period-to" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowNew(false)} disabled={creating}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating} data-testid="create-evaluation-btn">{creating ? 'Creating...' : 'Create'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
